import { Check, ChevronRight, LockKeyhole, Minus, Plus } from 'lucide-react';
import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCommerce } from '../contexts/CommerceContext';
import { imageSource } from '../lib/assets';
import { formatPrice } from '../lib/format';
import { createMercadoPagoCheckout, type CheckoutAddress } from '../services/checkout';
import { productFromCart } from '../services/products';

const blankAddress: CheckoutAddress = {
  cep: '',
  street: '',
  number: '',
  complement: '',
  neighborhood: '',
  city: '',
  state: '',
};

function paymentErrorMessage(error: unknown) {
  if (!(error instanceof Error)) return 'Não foi possível iniciar o pagamento. Tente novamente.';
  if (/Failed to send|FunctionsHttpError|non-2xx/i.test(error.message)) {
    return 'O pagamento ainda não está ativo no servidor. Verifique a função e o token do Mercado Pago no Supabase.';
  }
  return error.message;
}

export function CheckoutPage() {
  const { cart, products, updateQuantity, cartSubtotal, cartHasConsultation, user } = useCommerce();
  const [address, setAddress] = useState<CheckoutAddress>(blankAddress);
  const [payment, setPayment] = useState<'pix' | 'credit-card'>('pix');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const lines = cart
    .map((item) => ({ item, product: productFromCart(item.productId, products) }))
    .filter((line) => line.product);

  const lookupCep = async () => {
    const cep = address.cep.replace(/\D/g, '');
    if (cep.length !== 8) return;
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json() as {
        logradouro?: string;
        bairro?: string;
        localidade?: string;
        uf?: string;
        erro?: boolean;
      };
      if (!data.erro) {
        setAddress((current) => ({
          ...current,
          street: data.logradouro ?? current.street,
          neighborhood: data.bairro ?? current.neighborhood,
          city: data.localidade ?? current.city,
          state: data.uf ?? current.state,
        }));
      }
    } catch {
      setMessage('Não foi possível consultar esse CEP agora. Preencha o endereço manualmente.');
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setMessage('');

    if (!user) {
      setMessage('Entre na sua conta para continuar com segurança.');
      return;
    }
    if (cartHasConsultation) {
      setMessage('Há produtos sem preço definido. Confirme os valores com a equipe antes de finalizar.');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await createMercadoPagoCheckout(cart, address, payment);
      window.location.assign(result.checkoutUrl as string);
    } catch (error) {
      setMessage(paymentErrorMessage(error));
      setIsSubmitting(false);
    }
  };

  if (!lines.length) {
    return (
      <main className="page-shell checkout-page">
        <div className="empty-state">
          <p>Seu carrinho está vazio.</p>
          <Link className="button button-dark" to="/fitness">Explorar fitness</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="checkout-page page-shell">
      <header className="checkout-heading">
        <p className="eyebrow">Checkout</p>
        <h1>Finalize com tranquilidade.</h1>
        <div className="checkout-steps">
          <span className="is-current"><b>1</b> Identificação</span>
          <span><b>2</b> Entrega</span>
          <span><b>3</b> Pagamento</span>
          <span><b>4</b> Resumo</span>
        </div>
      </header>

      <form className="checkout-layout" onSubmit={submit}>
        <div className="checkout-form">
          <section>
            <h2>1. Identificação</h2>
            {user ? (
              <p>Compra identificada como <strong>{user.email}</strong>.</p>
            ) : (
              <p>
                Entre na sua conta para acompanhar este pedido e pagar com segurança.{' '}
                <Link className="text-link" to="/entrar">Entrar</Link>
              </p>
            )}
          </section>

          <section>
            <h2>2. Entrega</h2>
            <div className="address-grid">
              <label>CEP<input required value={address.cep} onBlur={lookupCep} onChange={(event) => setAddress({ ...address, cep: event.target.value })} inputMode="numeric" /></label>
              <label className="wide">Rua<input required value={address.street} onChange={(event) => setAddress({ ...address, street: event.target.value })} /></label>
              <label>Número<input required value={address.number} onChange={(event) => setAddress({ ...address, number: event.target.value })} /></label>
              <label>Complemento<input value={address.complement} onChange={(event) => setAddress({ ...address, complement: event.target.value })} /></label>
              <label>Bairro<input required value={address.neighborhood} onChange={(event) => setAddress({ ...address, neighborhood: event.target.value })} /></label>
              <label>Cidade<input required value={address.city} onChange={(event) => setAddress({ ...address, city: event.target.value })} /></label>
              <label>Estado<input required value={address.state} onChange={(event) => setAddress({ ...address, state: event.target.value.toUpperCase() })} maxLength={2} /></label>
            </div>
          </section>

          <section>
            <h2>3. Pagamento</h2>
            <div className="payment-options">
              <label className={payment === 'pix' ? 'is-selected' : ''}>
                <input type="radio" name="payment" value="pix" checked={payment === 'pix'} onChange={() => setPayment('pix')} />
                PIX <small>Confirmação rápida</small>
              </label>
              <label className={payment === 'credit-card' ? 'is-selected' : ''}>
                <input type="radio" name="payment" value="credit-card" checked={payment === 'credit-card'} onChange={() => setPayment('credit-card')} />
                Cartão de crédito <small>Processamento seguro</small>
              </label>
            </div>
            <p className="muted">Você será direcionada ao ambiente seguro do Mercado Pago para concluir.</p>
          </section>
        </div>

        <aside className="checkout-summary">
          <h2>4. Resumo</h2>
          {lines.map(({ item, product }) => product && (
            <article key={item.id}>
              <img src={imageSource(product.images[0])} alt="" />
              <div>
                <h3>{product.name}</h3>
                <p>{item.color} · {item.size}</p>
                <div className="quantity-control">
                  <button type="button" aria-label="Diminuir quantidade" onClick={() => updateQuantity(item.id, item.quantity - 1)}><Minus size={12} /></button>
                  <span>{item.quantity}</span>
                  <button type="button" aria-label="Aumentar quantidade" onClick={() => updateQuantity(item.id, item.quantity + 1)}><Plus size={12} /></button>
                </div>
              </div>
              <strong>{formatPrice((product.promotionalPrice ?? product.price ?? 0) * item.quantity)}</strong>
            </article>
          ))}
          <div className="summary-row"><span>Subtotal</span><strong>{cartHasConsultation ? 'A combinar' : formatPrice(cartSubtotal)}</strong></div>
          <div className="summary-row"><span>Frete</span><span>Informado no Mercado Pago</span></div>
          {cartHasConsultation && <p className="consultation-note">Os valores de produtos ainda são provisórios.</p>}
          {message && <p className="form-message" role="alert">{message}</p>}
          <button className="button button-dark full-width" type="submit" disabled={cartHasConsultation || isSubmitting}>
            <LockKeyhole size={16} />
            {cartHasConsultation ? 'Aguardando preços' : isSubmitting ? 'Abrindo pagamento...' : 'Ir para pagamento'}
            {!isSubmitting && <ChevronRight size={16} />}
          </button>
          <p className="secure-note"><Check size={15} /> Pagamento processado com segurança pelo Mercado Pago.</p>
        </aside>
      </form>
    </main>
  );
}
